import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactGA from 'react-ga4';
import SEO from '../components/SEO';
import '../styles/store-locator.css';
import { regions, stores, getStoreImage } from '../data/storeData';

const StoreLocator = () => {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [userLocation, setUserLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef(new Map());
  const userMarker = useRef(null);
  const storeListRef = useRef(null);
  const distanceCache = useRef(new Map());
  const updateTimeoutRef = useRef(null);
  const isMapInitialized = useRef(false);

  const filteredStores = useMemo(() => {
    return stores.filter(store => {
      const matchesRegion = !selectedRegion || store.region === selectedRegion;
      const matchesCity = !selectedCity || store.city.includes(selectedCity);
      const matchesSearch = !searchTerm || store.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesRegion && matchesCity && matchesSearch;
    });
  }, [selectedRegion, selectedCity, searchTerm]);

  const storeCountByRegion = useMemo(() => {
    return Object.keys(regions).reduce((acc, region) => {
      acc[region] = stores.filter(store => store.region === region).length;
      return acc;
    }, {});
  }, []);

  // 카카오 지도 초기화
  useEffect(() => {
    if (window.kakao?.maps) {
      initializeMap();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = '//dapi.kakao.com/v2/maps/sdk.js?appkey=9b32dbe49c429465cde9302aeb449cb7&autoload=false';
    document.head.appendChild(script);

    script.onload = () => {
      window.kakao.maps.load(() => {
        if (mapRef.current && !isMapInitialized.current) {
          const options = {
            center: new window.kakao.maps.LatLng(36.5, 127.5),
            level: 8
          };
          mapInstance.current = new window.kakao.maps.Map(mapRef.current, options);
          isMapInitialized.current = true;
          initializeMap();
        }
      });
    };

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // 필요한 마커만 생성 (지연 로딩)
  const getOrCreateMarker = useCallback((store) => {
    if (!window.kakao?.maps) return null;
    
    if (markers.current.has(store.id)) {
      return markers.current.get(store.id);
    }
    
    const position = new window.kakao.maps.LatLng(store.lat, store.lng);
    const marker = new window.kakao.maps.Marker({
      position: position,
      map: null
    });
    
    window.kakao.maps.event.addListener(marker, 'click', () => {
      selectStore(store);
      scrollToStore(store.id);
    });
    
    markers.current.set(store.id, marker);
    return marker;
  }, []);

  // 지도 초기화
  const initializeMap = useCallback(() => {
    if (!mapInstance.current || !window.kakao?.maps) return;

    setTimeout(() => {
      mapInstance.current.relayout();
      
      // 모든 마커 생성
      stores.forEach(store => {
        getOrCreateMarker(store);
      });
      
      // 지도 범위 설정
      setBoundsToFitStores(stores);
      updateMarkerVisibility();
    }, 100);
  }, []);

  // 지도 범위 설정 헬퍼 함수
  const setBoundsToFitStores = useCallback((storesToFit) => {
    if (!mapInstance.current || !window.kakao?.maps || storesToFit.length === 0) return;

    const bounds = new window.kakao.maps.LatLngBounds();
    storesToFit.forEach(store => {
      bounds.extend(new window.kakao.maps.LatLng(store.lat, store.lng));
    });
    
    if (!bounds.isEmpty()) {
      const latDiff = (bounds.getNorthEast().getLat() - bounds.getSouthWest().getLat()) * 0.1;
      const lngDiff = (bounds.getNorthEast().getLng() - bounds.getSouthWest().getLng()) * 0.1;
      
      const extendedBounds = new window.kakao.maps.LatLngBounds(
        new window.kakao.maps.LatLng(
          bounds.getSouthWest().getLat() - latDiff, 
          bounds.getSouthWest().getLng() - lngDiff
        ),
        new window.kakao.maps.LatLng(
          bounds.getNorthEast().getLat() + latDiff, 
          bounds.getNorthEast().getLng() + lngDiff
        )
      );
      
      mapInstance.current.setBounds(extendedBounds);
    }
  }, []);

  // 마커 보이기/숨기기 및 선택 상태 업데이트
  const updateMarkerVisibility = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (!mapInstance.current || !window.kakao?.maps) return;

      const visibleStoreIds = new Set(filteredStores.map(s => s.id));

      // 마커 상태 업데이트
      markers.current.forEach((marker, storeId) => {
        if (visibleStoreIds.has(storeId)) {
          marker.setMap(mapInstance.current);
          marker.setImage(null);
        } else {
          marker.setMap(null);
        }
      });

      // 선택된 매장으로 지도 중심 이동
      if (selectedStore && visibleStoreIds.has(selectedStore.id)) {
        const position = new window.kakao.maps.LatLng(selectedStore.lat, selectedStore.lng);
        mapInstance.current.setLevel(3);
        mapInstance.current.setCenter(position);
      }
    }, 100);
  }, [filteredStores, selectedStore]);

  // 현재 위치 가져오기
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('이 브라우저는 위치 서비스를 지원하지 않습니다.');
      return;
    }

    // 위치 검색 시작 로깅
    ReactGA.event({
      category: 'Location',
      action: 'Request User Location',
      label: 'My Location Button'
    });

    setIsLoadingLocation(true);
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000 // 5분 캐시
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        distanceCache.current.clear();
        
        // 위치 검색 성공 로깅
        ReactGA.event({
          category: 'Location',
          action: 'User Location Success',
          label: `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`
        });
        
        if (mapInstance.current && window.kakao?.maps) {
          const userPos = new window.kakao.maps.LatLng(latitude, longitude);
          
          // 사용자 위치 마커 업데이트
          if (userMarker.current) {
            userMarker.current.setMap(null);
          }
          
          userMarker.current = new window.kakao.maps.Marker({
            position: userPos,
            map: mapInstance.current,
            image: new window.kakao.maps.MarkerImage(
              'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#4285f4"><circle cx="12" cy="12" r="8" fill="#4285f4" stroke="white" stroke-width="2"/></svg>'),
              new window.kakao.maps.Size(24, 24)
            )
          });
          
          mapInstance.current.setLevel(5);
          mapInstance.current.setCenter(userPos);
        }
        setIsLoadingLocation(false);
      },
      (error) => {
        let message = '위치 정보를 가져올 수 없습니다.';
        let errorType = 'Unknown';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            message = '위치 접근이 거부되었습니다. 브라우저 설정에서 위치 권한을 허용해주세요.';
            errorType = 'Permission Denied';
            break;
          case error.POSITION_UNAVAILABLE:
            message = '위치 정보를 사용할 수 없습니다.';
            errorType = 'Position Unavailable';
            break;
          case error.TIMEOUT:
            message = '위치 정보 요청이 시간 초과되었습니다. 다시 시도해주세요.';
            errorType = 'Timeout';
            break;
        }
        
        // 위치 검색 실패 로깅
        ReactGA.event({
          category: 'Location',
          action: 'User Location Failed',
          label: errorType
        });
        
        alert(message);
        setIsLoadingLocation(false);
      },
      options
    );
  }, []);

  // 거리 계산 (캐싱 적용)
  const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const cacheKey = `${lat1},${lng1},${lat2},${lng2}`;
    if (distanceCache.current.has(cacheKey)) {
      return distanceCache.current.get(cacheKey);
    }
    
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    distanceCache.current.set(cacheKey, distance);
    return distance;
  }, []);

  // 매장으로 스크롤
  const scrollToStore = (storeId) => {
    setTimeout(() => {
      const element = document.getElementById(`store-${storeId}`);
      if (element && storeListRef.current) {
        const offsetTop = element.offsetTop - storeListRef.current.offsetTop;
        storeListRef.current.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }, 100);
  };

  // 매장 선택 및 지도 중심 이동
  const selectStore = (store) => {
    // Google Analytics 이벤트 로깅
    ReactGA.event({
      category: 'Store',
      action: 'Select Store',
      label: store.name
    });
    
    // 같은 매장을 다시 클릭하면 선택 해제
    if (selectedStore?.id === store.id) {
      setSelectedStore(null);
    } else {
      setSelectedStore(store);
      setOverlayVisible(true);
    }
  };

  // 필터된 매장이 변경될 때 마커 및 지도 범위 업데이트
  useEffect(() => {
    updateMarkerVisibility();
    
    // 선택된 매장이 없고 필터된 매장이 있을 때만 범위 조정
    if (!selectedStore && filteredStores.length > 0 && isMapInitialized.current) {
      setBoundsToFitStores(filteredStores);
    }
  }, [filteredStores, selectedStore, updateMarkerVisibility, setBoundsToFitStores]);

  // 거리순 정렬된 매장 목록
  const sortedStores = useMemo(() => {
    if (!userLocation) return filteredStores;
    return [...filteredStores].sort((a, b) => {
      const distA = calculateDistance(userLocation.lat, userLocation.lng, a.lat, a.lng);
      const distB = calculateDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
      return distA - distB;
    });
  }, [filteredStores, userLocation, calculateDistance]);

  return (
    <>
      <SEO 
        title="지점 찾기 - 한국국민금거래소"
        description="한국국민금거래소 전국 지점 안내. 서울, 경기, 경남 등 전국 지점 위치, 연락처, 영업시간 확인"
        keywords="한국국민금거래소 지점, 금거래소 위치, 금거래소 연락처, 금매입 지점, 금판매 지점, 한국국민금거래소"
        url="https://koreakookmingold.com/store-locator"
      />
      <div className="store-locator">
      <section className="store-locator__hero">
        <div className="store-locator__hero-content">
          <h1 className="store-locator__hero-title">지점 찾기</h1>
          <p className="store-locator__hero-subtitle">
            한국국민금거래소와 함께<br />
            가까운 지점을 찾아보세요
          </p>
        </div>
      </section>

      <section className="store-locator__section">
        <div className="store-locator__container">
          <div className="store-locator__section-header">
            <h2 className="store-locator__section-title">한국국민금거래소 지점을 찾아보세요</h2>
            <div className="store-locator__section-divider"></div>
          </div>

          <div className="store-locator__regions">
            <ul className="store-locator__region-list">
              <li className="store-locator__region-item">
                <button 
                  onClick={getCurrentLocation} 
                  disabled={isLoadingLocation}
                  className="store-locator__region-link store-locator__location-link"
                >
                  {isLoadingLocation ? '📍찾는중...' : '📍 내 주변'}
                </button>
              </li>
              <li className={`store-locator__region-item ${!selectedRegion ? 'store-locator__region-item--active' : ''}`}>
                <button onClick={() => { 
                  setSelectedRegion(''); 
                  setSelectedStore(null);
                  ReactGA.event({
                    category: 'Filter',
                    action: 'Select Region',
                    label: '전체'
                  });
                }} className="store-locator__region-link">
                  전체 ({stores.length})
                </button>
              </li>
              {Object.keys(regions).map(region => {
                const count = storeCountByRegion[region];
                if (count === 0) return null;
                return (
                  <li key={region} className={`store-locator__region-item ${selectedRegion === region ? 'store-locator__region-item--active' : ''}`}>
                    <button onClick={() => { 
                      setSelectedRegion(region); 
                      setSelectedStore(null);
                      ReactGA.event({
                        category: 'Filter',
                        action: 'Select Region',
                        label: region
                      });
                    }} className="store-locator__region-link">
                      {region} ({count})
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="store-locator__map-section">
            <div className="store-locator__store-list" ref={storeListRef}>
              {sortedStores.length === 0 ? (
                <div className="store-locator__empty-state">
                  <h3 className="store-locator__empty-title">매장을 찾을 수 없습니다</h3>
                </div>
              ) : (
                <ul>
                  {sortedStores.map((store) => {
                    const isSelected = selectedStore?.id === store.id;
                    return (
                      <li key={store.id} className="store-locator__store-item" id={`store-${store.id}`}>
                        <div className="store-locator__store-header" onClick={() => {
                          selectStore(store);
                          scrollToStore(store.id);
                        }}>
                          <div className="store-locator__store-header-content">
                            <h4 className="store-locator__store-name">
                              {isSelected ? (
                                <img src="/images/logo-badge.png" alt="선택된 매장" 
                                     className="store-locator__store-badge" />
                              ) : (
                                <span className="store-locator__store-badge-placeholder"></span>
                              )}
                              {store.name}
                            </h4>
                            {userLocation && (
                              <span className="store-locator__store-distance">
                                {calculateDistance(userLocation.lat, userLocation.lng, store.lat, store.lng).toFixed(1)}km
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="store-locator__store-info">
                            <div className="store-locator__info-row">
                              <span className="store-locator__info-label">주소</span>
                              <span className="store-locator__info-value">
                                <a 
                                  href={`https://map.kakao.com/link/to/${encodeURIComponent(store.name)},${store.lat},${store.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="store-locator__clickable-link"
                                >
                                  {store.address}
                                </a>
                              </span>
                            </div>
                            <div className="store-locator__info-row">
                              <span className="store-locator__info-label">전화번호</span>
                              <span className="store-locator__info-value">
                                <a 
                                  href={`tel:${store.phone.replace(/[^0-9]/g, '')}`}
                                  className="store-locator__clickable-link"
                                >
                                  {store.phone}
                                </a>
                              </span>
                            </div>
                            <div className="store-locator__info-row">
                              <span className="store-locator__info-label">영업시간</span>
                              <span className="store-locator__info-value">{store.hours}</span>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            
            <div className="store-locator__map">
              <div ref={mapRef} className="store-locator__map-container"></div>
              {selectedStore && !overlayVisible && (
                <button 
                  className="store-locator__show-overlay"
                  onClick={() => setOverlayVisible(true)}
                >
                  매장 정보 보기
                </button>
              )}
              {selectedStore && overlayVisible && (
                <div className="store-locator__map-overlay">
                  <div className="store-locator__overlay-content">
                    <button 
                      className="store-locator__overlay-close"
                      onClick={() => setOverlayVisible(false)}
                    >
                    </button>
                    <img 
                      src={getStoreImage(selectedStore.id, 'main')} 
                      alt={`${selectedStore.name} 매장 사진`}
                      className="store-locator__overlay-image"
                      onError={(e) => {
                        e.target.src = '/images/stores/default.jpg';
                      }}
                    />
                    <div className="store-locator__overlay-title-row">
                      <a 
                        href={`tel:${selectedStore.phone.replace(/[^0-9]/g, '')}`}
                        className="store-locator__overlay-icon-btn"
                        onClick={() => {
                          ReactGA.event({
                            category: 'Store',
                            action: 'Call Store',
                            label: selectedStore.name
                          });
                        }}
                      >
                        <img src="/images/map/call.png" alt="전화" />
                      </a>
                      <h3 className="store-locator__overlay-title">{selectedStore.name}</h3>
                      <a 
                        href={`https://map.kakao.com/link/to/${encodeURIComponent(selectedStore.name)},${selectedStore.lat},${selectedStore.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="store-locator__overlay-icon-btn"
                        onClick={() => {
                          ReactGA.event({
                            category: 'Store',
                            action: 'Get Directions',
                            label: selectedStore.name
                          });
                        }}
                      >
                        <img src="/images/map/map.png" alt="길찾기" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default StoreLocator;